import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { iniciativasAPI } from '../services/api';

const ProjectsList = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [innovacionFilter, setInnovacionFilter] = useState('');
  const [enUsoEscaladoFilter, setEnUsoEscaladoFilter] = useState('');
  const [productivizadoFilter, setProductivizadoFilter] = useState('');
  const [filteredProjects, setFilteredProjects] = useState([]);

  useEffect(() => {
    loadProjects();
  }, []);

  useEffect(() => {
    // Filter projects based on search term and status filter
    let filtered = projects.filter(project =>
      project.nombre_de_la_oportunidad.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (project.negocio_estructura_detail?.negocio && project.negocio_estructura_detail.negocio.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (project.negocio_estructura_detail?.subnegocio && project.negocio_estructura_detail.subnegocio.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (project.tecnologia_nombre && project.tecnologia_nombre.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (project.area_ejecucion_nombre && project.area_ejecucion_nombre.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (project.responsable_comercial_nombre && project.responsable_comercial_nombre.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (project.lider_tecnico_nombre && project.lider_tecnico_nombre.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (project.jefe_proyecto_nombre && project.jefe_proyecto_nombre.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (project.estado_display && project.estado_display.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    if (statusFilter) {
      filtered = filtered.filter(project => project.estado === statusFilter);
    }

    if (innovacionFilter === 'true') {
      filtered = filtered.filter(project => project.innovacion === true);
    } else if (innovacionFilter === 'false') {
      filtered = filtered.filter(project => project.innovacion === false);
    }

    if (enUsoEscaladoFilter === 'true') {
      filtered = filtered.filter(project => project.en_uso_escalado === true);
    } else if (enUsoEscaladoFilter === 'false') {
      filtered = filtered.filter(project => project.en_uso_escalado === false);
    }

    if (productivizadoFilter === 'true') {
      filtered = filtered.filter(project => project.productivizado === true);
    } else if (productivizadoFilter === 'false') {
      filtered = filtered.filter(project => project.productivizado === false);
    }

    setFilteredProjects(filtered);
  }, [projects, searchTerm, statusFilter, innovacionFilter, enUsoEscaladoFilter, productivizadoFilter]);

  const loadProjects = async () => {
    try {
      setLoading(true);
      // Cargar todos los proyectos sin paginación para asegurar que se muestren todos
      let allProjects = [];
      let nextUrl = null;
      let page = 1;
      
      do {
        const params = page === 1 ? {} : { page };
        const response = await iniciativasAPI.getAll(params);
        const data = response.data;
        
        if (data.results) {
          // Respuesta paginada
          allProjects = [...allProjects, ...data.results];
          nextUrl = data.next;
          page++;
        } else if (Array.isArray(data)) {
          // Respuesta directa (sin paginación)
          allProjects = data;
          nextUrl = null;
        } else {
          // Formato inesperado
          console.error('Formato de respuesta inesperado:', data);
          allProjects = [];
          nextUrl = null;
        }
        
        // Evitar bucle infinito
        if (page > 100) {
          console.warn('Se alcanzó el límite de páginas (100)');
          break;
        }
      } while (nextUrl);
      
      console.log(`Total proyectos cargados: ${allProjects.length}`);
      setProjects(allProjects);
    } catch (err) {
      setError('Error al cargar los proyectos');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No definida';
    return new Date(dateString).toLocaleDateString('es-ES');
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'No definido';
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const getEstadoBadgeClass = (estado) => {
    switch (estado) {
      case 'oportunidad_identificada':
        return 'bg-info';
      case 'propuesta':
        return 'bg-warning';
      case 'aprobado':
        return 'bg-success';
      case 'en_curso':
        return 'bg-primary';
      case 'finalizada':
        return 'bg-secondary';
      case 'cancelada':
        return 'bg-danger';
      case 'rechazada':
        return 'bg-dark';
      default:
        return 'bg-light text-dark';
    }
  };

  if (loading) {
    return <div className="loading">Cargando proyectos...</div>;
  }

  if (error) {
    return <div className="alert alert-error">{error}</div>;
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>Lista de Proyectos</h1>
        <div className="d-flex align-items-center">
          <select
            className="form-control"
            style={{ width: '200px', marginRight: '10px' }}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">Todos los estados</option>
            <option value="oportunidad_identificada">Oportunidad identificada</option>
            <option value="estudio_impacto">Estudio de impacto</option>
            <option value="propuesta">Propuesta</option>
            <option value="validacion_lider_tecnico">Validación Líder Técnico</option>
            <option value="supervision">Supervisión</option>
            <option value="pendiente_aprobacion_negocio">Pendiente de aprobación</option>
            <option value="rechazada">Rechazada</option>
            <option value="aprobado">Aprobado</option>
            <option value="planificada">Planificada</option>
            <option value="en_curso">En curso</option>
            <option value="bloqueada">Bloqueada</option>
            <option value="cancelada">Cancelada</option>
            <option value="finalizada">Finalizada</option>
          </select>
          <select
            className="form-control"
            style={{ width: '150px', marginRight: '10px' }}
            value={innovacionFilter}
            onChange={(e) => setInnovacionFilter(e.target.value)}
          >
            <option value="">Innovación: Todos</option>
            <option value="true">Con Innovación</option>
            <option value="false">Sin Innovación</option>
          </select>
          <select
            className="form-control"
            style={{ width: '150px', marginRight: '10px' }}
            value={enUsoEscaladoFilter}
            onChange={(e) => setEnUsoEscaladoFilter(e.target.value)}
          >
            <option value="">En uso: Todos</option>
            <option value="true">En uso/escalado</option>
            <option value="false">No en uso</option>
          </select>
          <select
            className="form-control"
            style={{ width: '150px', marginRight: '10px' }}
            value={productivizadoFilter}
            onChange={(e) => setProductivizadoFilter(e.target.value)}
          >
            <option value="">Productivizado: Todos</option>
            <option value="true">Productivizado</option>
            <option value="false">No productivizado</option>
          </select>
          <select
            className="form-control"
            style={{ width: '150px', marginRight: '10px' }}
            value={productivizadoFilter}
            onChange={(e) => setProductivizadoFilter(e.target.value)}
          >
            <option value="">Productivizado: Todos</option>
            <option value="true">Productivizado</option>
            <option value="false">No productivizado</option>
          </select>
          <input
            type="text"
            placeholder="Buscar proyectos..."
            className="form-control"
            style={{ width: '300px', marginRight: '10px' }}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <span className="text-muted">
            {filteredProjects.length} proyecto{filteredProjects.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {filteredProjects.length === 0 ? (
        <div className="alert alert-info">
          {searchTerm ? 'No se encontraron proyectos que coincidan con la búsqueda.' : 'No hay proyectos disponibles.'}
        </div>
      ) : (
        <div className="row">
          {filteredProjects.map(project => (
            <div key={project.id} className="col-md-6 mb-4">
              <div className="card">
                <div className="card-header">
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <h3 className="card-title">
                        <Link 
                          to={`/projects/${project.id}`}
                          style={{ textDecoration: 'none', color: 'inherit' }}
                        >
                          {project.nombre_de_la_oportunidad}
                        </Link>
                      </h3>
                      <p className="card-subtitle">
                        {project.negocio_estructura_detail ? 
                          `${project.negocio_estructura_detail.negocio} • ${project.negocio_estructura_detail.subnegocio}` : 
                          'Sin información de negocio'
                        }
                      </p>
                    </div>
                    {/* Status and Classifications */}
                    <div className="d-flex flex-column align-items-end">
                      {project.estado_display && (
                        <span 
                          style={{
                            backgroundColor: '#f8f9fa',
                            color: '#495057',
                            border: '1px solid #dee2e6',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: 'normal',
                            marginBottom: '4px'
                          }}
                        >
                          {project.estado_display}
                        </span>
                      )}
                      <div className="d-flex gap-1">
                        {project.innovacion && (
                          <span 
                            style={{
                              backgroundColor: '#28a745',
                              color: 'white',
                              padding: '2px 6px',
                              borderRadius: '12px',
                              fontSize: '10px',
                              fontWeight: 'bold'
                            }}
                          >
                            INNOVACIÓN
                          </span>
                        )}
                        {project.en_uso_escalado && (
                          <span 
                            style={{
                              backgroundColor: '#007bff',
                              color: 'white',
                              padding: '2px 6px',
                              borderRadius: '12px',
                              fontSize: '10px',
                              fontWeight: 'bold'
                            }}
                          >
                            EN USO/ESCALADO
                          </span>
                        )}
                        {project.productivizado && (
                          <span 
                            style={{
                              backgroundColor: '#6f42c1',
                              color: 'white',
                              padding: '2px 6px',
                              borderRadius: '12px',
                              fontSize: '10px',
                              fontWeight: 'bold'
                            }}
                          >
                            PRODUCTIVIZADO
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                
                <div>
                  {/* Project Description */}
                  {project.descripcion && (
                    <p className="mb-3" style={{ 
                      maxHeight: '60px', 
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>
                      {project.descripcion.length > 150 
                        ? project.descripcion.substring(0, 150) + '...' 
                        : project.descripcion}
                    </p>
                  )}

                  {/* Project Details */}
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <small className="text-muted">Tecnología:</small>
                      <div>{project.tecnologia_nombre || 'No asignada'}</div>
                    </div>
                    <div className="col-md-6">
                      <small className="text-muted">Área de Ejecución:</small>
                      <div>{project.area_ejecucion_nombre || 'No asignada'}</div>
                    </div>
                  </div>

                  <div className="row mb-3">
                    <div className="col-md-6">
                      <small className="text-muted">Responsable DN:</small>
                      <div>{project.responsable_comercial_nombre || 'No asignado'}</div>
                    </div>
                    <div className="col-md-6">
                      <small className="text-muted">Estado:</small>
                      <span className={`badge ${getEstadoBadgeClass(project.estado)}`}>
                        {project.estado_display}
                      </span>
                    </div>
                  </div>

                  {/* Additional Project Info */}
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <small className="text-muted">Líder Técnico:</small>
                      <div>{project.lider_tecnico_nombre || 'No asignado'}</div>
                    </div>
                    <div className="col-md-6">
                      <small className="text-muted">Jefe de Proyecto:</small>
                      <div>{project.jefe_proyecto_nombre || 'No asignado'}</div>
                    </div>
                  </div>

                  {/* Dates and Values */}
                  <div className="row mb-3">
                    <div className="col-md-4">
                      <small className="text-muted">Fecha de Entrega:</small>
                      <div>{formatDate(project.fecha_de_entrega_propuesta_prevista)}</div>
                    </div>
                    <div className="col-md-4">
                      <small className="text-muted">Porcentaje de Avance:</small>
                      <div>
                        {project.porcentaje_avance !== null && project.porcentaje_avance !== undefined ? 
                          `${project.porcentaje_avance}%` : 
                          'No definido'}
                      </div>
                    </div>
                    <div className="col-md-4">
                      <small className="text-muted">Valor Estimado:</small>
                      <div>
                        {project.valor_minimo_estimado && project.valor_maximo_estimado ? (
                          <>
                            {formatCurrency(project.valor_minimo_estimado)} - {formatCurrency(project.valor_maximo_estimado)}
                          </>
                        ) : (
                          'No definido'
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Planning and Execution Dates */}
                  <div className="row mb-3">
                    <div className="col-md-3">
                      <small className="text-muted">Inicio Planificado:</small>
                      <div style={{fontSize: '13px'}}>{formatDate(project.fecha_de_inicio_planificada)}</div>
                    </div>
                    <div className="col-md-3">
                      <small className="text-muted">Inicio Real:</small>
                      <div style={{fontSize: '13px'}}>{formatDate(project.fecha_de_inicio_real)}</div>
                    </div>
                    <div className="col-md-3">
                      <small className="text-muted">Fin Planificado:</small>
                      <div style={{fontSize: '13px'}}>{formatDate(project.fecha_de_fin_planificada)}</div>
                    </div>
                    <div className="col-md-3">
                      <small className="text-muted">Fin Real:</small>
                      <div style={{fontSize: '13px'}}>{formatDate(project.fecha_de_fin_real)}</div>
                    </div>
                  </div>

                  {/* Team Info */}
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <small className="text-muted">Equipo:</small>
                      <div>
                        <span style={{ 
                          background: '#dc3545', 
                          color: 'white', 
                          padding: '2px 6px', 
                          borderRadius: '12px', 
                          fontSize: '12px' 
                        }}>
                          {project.total_personas || 0} persona{(project.total_personas || 0) !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>
                    
                    <div>
                      <Link to={`/projects/${project.id}`} className="btn btn-primary btn-sm">
                        Ver Detalles
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Summary Stats */}
      <div className="card mt-4">
        <div className="card-header">
          <h4 className="card-title">Resumen de Proyectos</h4>
        </div>
        <div>
          <div className="row">
            <div className="col-md-3 text-center">
              <h3 style={{ color: '#dc3545', margin: 0 }}>{filteredProjects.length}</h3>
              <small className="text-muted">Total Proyectos</small>
            </div>
            <div className="col-md-3 text-center">
              <h3 style={{ color: '#666666', margin: 0 }}>
                {filteredProjects.reduce((sum, p) => sum + (p.total_personas || 0), 0)}
              </h3>
              <small className="text-muted">Total Personas Involucradas</small>
            </div>
            <div className="col-md-3 text-center">
              <h3 style={{ color: '#dc3545', margin: 0 }}>
                {filteredProjects.filter(p => p.fecha_de_entrega_propuesta_prevista && 
                  new Date(p.fecha_de_entrega_propuesta_prevista) > new Date()).length}
              </h3>
              <small className="text-muted">Proyectos Activos</small>
            </div>
            <div className="col-md-3 text-center">
              <h3 style={{ color: '#dc3545', margin: 0 }}>
                {formatCurrency(
                  filteredProjects.reduce((sum, p) => 
                    sum + (parseFloat(p.valor_maximo_estimado) || 0), 0)
                ).replace('EUR', '').trim()}€
              </h3>
              <small className="text-muted">Valor Total Estimado</small>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectsList;